/**
 * product-variants-custom.js
 *
 * Maneja el selector de variantes personalizadas basado en el metacampo
 * `parent_link.data`. Al hacer clic en un producto alternativo, obtiene
 * el HTML de la sección vía la Section Rendering API de Shopify,
 * reemplaza el elemento <product-info> completo en el DOM y actualiza
 * la URL, todo con una transición de fade para evitar saltos visuales.
 */

(function () {
  // Prevenir doble inicialización tras el replaceWith
  if (window.productVariantsCustomInit) return;
  window.productVariantsCustomInit = true;

  const FADE_MS = 180;

  /** Hace fade-out del elemento vía inline style, resuelve al terminar */
  function fadeOut(el) {
    return new Promise((resolve) => {
      el.style.transition = `opacity ${FADE_MS}ms ease`;
      el.style.opacity = '0';
      el.style.pointerEvents = 'none';
      setTimeout(resolve, FADE_MS);
    });
  }

  /** Hace fade-in del elemento vía inline style, resuelve al terminar */
  function fadeIn(el) {
    return new Promise((resolve) => {
      // Garantizamos dos frames antes de iniciar para que el browser
      // pinte el elemento en opacity:0 primero
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.style.transition = `opacity ${FADE_MS}ms ease`;
          el.style.opacity = '1';
          el.style.pointerEvents = '';
          setTimeout(resolve, FADE_MS);
        });
      });
    });
  }

  /** Obtiene el HTML de la sección del nuevo producto */
  async function fetchSection(productUrl, sectionId) {
    const url = `${productUrl}?sections=${sectionId}`;
    const response = await fetch(url, { method: 'GET' });
    if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
    const data = await response.json();
    if (!data[sectionId]) throw new Error(`Section "${sectionId}" not found in response`);
    return data[sectionId];
  }

  /** Parsea el HTML de la sección y devuelve product-info, galerías y modal */
  function parseSection(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const productInfo = doc.querySelector('product-info');
    if (!productInfo) throw new Error('No se encontró <product-info> en la respuesta');

    // Eliminar solo scripts ejecutables para evitar doble inicialización de custom elements.
    // Se conservan los <script type="application/json"> que contienen datos de variantes, etc.
    productInfo.querySelectorAll('script:not([type="application/json"])').forEach((s) => s.remove());

    return {
      productInfo,
      galleries: Array.from(doc.querySelectorAll('media-gallery')),
      modal: doc.querySelector('product-modal'),
    };
  }

  /** Captura el orden canónico de los handles desde el DOM inicial */
  let canonicalOrder = null;

  function captureCanonicalOrder() {
    const list = document.querySelector('.product-variants-custom__list');
    if (!list) return;
    canonicalOrder = Array.from(
      list.querySelectorAll('.product-variants-custom__btn[data-product-handle]')
    ).map((btn) => btn.dataset.productHandle);
  }

  /** Reordena las miniaturas del nuevo product-info según el orden canónico */
  function reorderThumbnails(newProductInfo) {
    if (!canonicalOrder || canonicalOrder.length === 0) return;
    const list = newProductInfo.querySelector('.product-variants-custom__list');
    if (!list) return;

    const itemMap = new Map();
    list.querySelectorAll('.product-variants-custom__item').forEach((li) => {
      const btn = li.querySelector('.product-variants-custom__btn[data-product-handle]');
      if (btn) itemMap.set(btn.dataset.productHandle, li);
    });

    // Re-insertar en orden canónico
    canonicalOrder.forEach((handle) => {
      const li = itemMap.get(handle);
      if (li) {
        list.appendChild(li);
        itemMap.delete(handle);
      }
    });

    // Cualquier extra (no estaba en el orden inicial) va al final
    itemMap.forEach((li) => list.appendChild(li));
  }

  /** Lógica principal de swap: recibe una URL de producto y actualiza el DOM */
  async function switchProduct(productUrl) {
    if (!productUrl) return;

    const productInfo = document.querySelector('product-info');
    if (!productInfo) return;

    const sectionId = productInfo.dataset.section;

    // Bloquear todos los botones de variante durante la transición
    const allBtns = document.querySelectorAll('.product-variants-custom__btn');
    allBtns.forEach((b) => (b.disabled = true));

    try {
      // Lanzar el fade-out y el fetch EN PARALELO para minimizar tiempo total
      const [, sectionHtml] = await Promise.all([
        fadeOut(productInfo),
        fetchSection(productUrl, sectionId),
      ]);

      const { productInfo: newProductInfo, galleries: newGalleries, modal: newModal } = parseSection(sectionHtml);

      // Actualizar galerías y modal que estén FUERA de product-info
      // (en layouts donde product-info no envuelve la galería de medios)
      newGalleries.forEach((newGallery) => {
        const existing = document.getElementById(newGallery.id);
        if (existing && !existing.closest('product-info')) existing.replaceWith(newGallery);
      });
      if (newModal) {
        const existingModal = document.getElementById(newModal.id);
        if (existingModal && !existingModal.closest('product-info')) existingModal.replaceWith(newModal);
      }

      // Restaurar el orden canónico antes de insertar en el DOM
      reorderThumbnails(newProductInfo);

      // El nuevo elemento empieza invisible para evitar el flash
      newProductInfo.style.opacity = '0';
      newProductInfo.style.transition = 'none';
      newProductInfo.style.pointerEvents = 'none';

      // Reemplazar el elemento — dispara connectedCallback en todos los
      // web components hijos (variant-selects, product-form, etc.)
      productInfo.replaceWith(newProductInfo);

      // Hacer fade-in del nuevo contenido
      await fadeIn(newProductInfo);

    } catch (err) {
      console.error('[product-variants-custom] Error al cambiar producto:', err);

      // Restaurar visibilidad si algo falla
      productInfo.style.opacity = '1';
      productInfo.style.pointerEvents = '';
      allBtns.forEach((b) => (b.disabled = false));
    }
  }

  // Capturar orden canónico y registrar estado inicial
  captureCanonicalOrder();
  history.replaceState({ productUrl: location.pathname }, '');

  /** Event delegation en document para capturar clics incluso tras replaceWith */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.product-variants-custom__btn');
    if (!btn) return;

    // No hacer nada si ya es el producto activo
    const item = btn.closest('.product-variants-custom__item');
    if (item && item.classList.contains('is-active')) return;

    // No hacer nada si el botón está deshabilitado
    if (btn.disabled) return;

    e.preventDefault();

    const productUrl = btn.dataset.productUrl;
    history.replaceState({ productUrl }, '', productUrl);
    switchProduct(productUrl);
  });

  /** Navegación atrás/adelante del browser */
  window.addEventListener('popstate', (e) => {
    const productUrl = e.state?.productUrl || location.pathname;
    switchProduct(productUrl);
  });
})();

/* ============================================================
   Card Variants Custom — swap de producto en tarjeta de colección
   Usa el Product JSON API de Shopify para actualizar el card
   en el DOM sin navegar a la página del producto.
   ============================================================ */
(function () {
  if (window.cardVariantsCustomInit) return;
  window.cardVariantsCustomInit = true;

  const FADE_MS = 150;

  // Detect thousands separator from existing rendered prices (handles locale differences)
  var _thousandsSep = (function () {
    var el = document.querySelector('.price-item');
    if (!el) return ',';
    var raw = el.textContent.replace(/[^0-9.,]/g, '');
    return /\d\.\d{3}(?!\d)/.test(raw) ? '.' : ',';
  })();

  /** Convierte centavos al formato de moneda del shop (implementación oficial de Shopify) */
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var formatString = window.shopMoneyFormat || '{{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      if (precision === undefined) precision = 2;
      if (thousands === undefined) thousands = _thousandsSep;
      if (decimal === undefined) decimal = _thousandsSep === '.' ? ',' : '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollarsStr = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsStr = parts[1] ? decimal + parts[1] : '';
      return dollarsStr + centsStr;
    }

    var match = formatString.match(placeholderRegex);
    if (!match) return (cents / 100).toFixed(2);

    var value;
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  /** Añade sufijo de tamaño a una URL de imagen de Shopify CDN */
  function shopifyImgUrl(src, size) {
    if (!src) return '';
    if (typeof src === 'object') src = src.src || '';
    // Eliminar cualquier sufijo de tamaño previo, luego añadir el nuevo
    const clean = src.replace(/_\d+x\d*(?=\.[a-z]+)/i, '');
    return clean.replace(/(\.[a-z]+)(\?.*)?$/i, `_${size}x$1$2`);
  }

  /** Actualiza las imágenes del card (principal y hover) */
  function updateImages(cardWrapper, data) {
    const media = cardWrapper.querySelector('.card__media .media');
    if (!media) return;
    const imgs = media.querySelectorAll('img');

    if (data.featured_image && imgs[0]) {
      imgs[0].src = shopifyImgUrl(data.featured_image, 533);
      imgs[0].removeAttribute('srcset');
      imgs[0].alt = data.title;
    }

    if (imgs[1]) {
      const second = data.images && data.images[1];
      if (second) {
        imgs[1].src = shopifyImgUrl(second, 533);
        imgs[1].removeAttribute('srcset');
        imgs[1].alt = data.title;
        imgs[1].style.visibility = '';
      } else {
        imgs[1].style.visibility = 'hidden';
      }
    }
  }

  /** Actualiza todos los enlaces de producto y los textos de título */
  function updateLinksAndTitles(cardWrapper, url, title) {
    cardWrapper.querySelectorAll('a:not(.card-variants-custom__btn)').forEach((a) => {
      a.href = url;
    });
    cardWrapper.querySelectorAll('.card__heading a').forEach((a) => {
      a.textContent = title;
    });
  }

  /** Actualiza el texto del vendor y el gender del producto */
  function updateVendor(cardWrapper, vendor, gender) {
    const el = cardWrapper.querySelector('.card__information__type');
    if (!el) return;
    el.textContent = vendor;
    if (gender) el.appendChild(document.createTextNode(' - ' + gender));
  }

  /** Aplica el prefijo "A partir de" si el producto tiene precios variables */
  function applyFromPrice(formatted, priceVaries) {
    const template = (window.cardVariantsI18n || {}).from_price;
    if (priceVaries && template) return template.replace('[price]', formatted);
    return formatted;
  }

  /** Actualiza el bloque de precio */
  function updatePrice(cardWrapper, price, compareAtPrice, priceVaries) {
    const priceEl = cardWrapper.querySelector('.price');
    if (!priceEl) return;

    const isOnSale = !!compareAtPrice && compareAtPrice > price;

    priceEl.classList.toggle('price--on-sale', isOnSale);
    priceEl.classList.remove('price--sold-out');

    const displayPrice = applyFromPrice(formatMoney(price), priceVaries);

    // Precio regular (visible cuando no hay descuento)
    const regularItem = priceEl.querySelector('.price__regular .price-item--regular');
    if (regularItem) regularItem.textContent = displayPrice;

    // Precio de oferta
    const saleItem = priceEl.querySelector('.price__sale .price-item--sale');
    if (saleItem) saleItem.textContent = displayPrice;

    // Precio tachado (compare_at)
    const compareItem = priceEl.querySelector('.price__sale s.price-item--regular');
    if (compareItem) compareItem.textContent = isOnSale ? formatMoney(compareAtPrice) : '';

    // Badge de % dentro del snippet price (si show_badges estaba activo)
    const saleBadge = priceEl.querySelector('.price__badge-sale');
    if (saleBadge) {
      if (isOnSale) {
        const pct = Math.floor(((compareAtPrice - price) / compareAtPrice) * 100);
        saleBadge.textContent = `${pct}%`;
        saleBadge.style.display = '';
      } else {
        saleBadge.style.display = 'none';
      }
    }
  }

  /**
   * Actualiza los badges de la tarjeta (.card__badge).
   * Hay dos: uno dentro de .card__inner (muestra %) y uno fuera (muestra texto).
   */
  function updateBadges(cardWrapper, available, price, compareAtPrice) {
    const i18n = window.cardVariantsI18n || {};
    const soldOutColor = cardWrapper.dataset.soldOutColor || '';
    const saleColor = cardWrapper.dataset.saleColor || '';
    const isOnSale = !!compareAtPrice && compareAtPrice > price && available;
    const pct = isOnSale
      ? Math.floor(((compareAtPrice - price) / compareAtPrice) * 100)
      : 0;

    cardWrapper.querySelectorAll('.card__badge').forEach((badgeWrap) => {
      const isInner = !!badgeWrap.closest('.card__inner');
      let span = badgeWrap.querySelector('.badge');

      if (!available) {
        if (!span) {
          span = document.createElement('span');
          badgeWrap.appendChild(span);
        }
        span.className = `badge badge--bottom-left color-${soldOutColor}`;
        span.textContent = i18n.sold_out || 'Sold out';
      } else if (isOnSale) {
        if (!span) {
          span = document.createElement('span');
          badgeWrap.appendChild(span);
        }
        span.className = `badge badge--bottom-left color-${saleColor}`;
        span.textContent = isInner ? `${pct}%` : (i18n.on_sale || 'Sale');
      } else {
        if (span) span.remove();
      }
    });
  }

  /**
   * Actualiza el quick-add para que apunte al nuevo producto.
   * - Multi-variante: actualiza data-product-url en el opener.
   * - Single-variante: actualiza el variant ID en el input oculto y el estado del botón.
   */
  function updateQuickAdd(cardWrapper, data) {
    // Caso multi-variante: modal carga el form dinámicamente vía data-product-url
    const openerBtn = cardWrapper.querySelector('modal-opener button[data-product-url]');
    if (openerBtn) {
      openerBtn.dataset.productUrl = data.url || `/products/${data.handle}`;
    }

    // Caso single-variante: form directo con variant ID hardcodeado
    const variantInput = cardWrapper.querySelector('.product-variant-id');
    if (variantInput) {
      const variant = data.variants.find((v) => v.available) || data.variants[0];
      if (!variant) return;

      variantInput.value = variant.id;
      variantInput.disabled = !variant.available;

      const submitBtn = variantInput.closest('form')?.querySelector('[data-sold-out-message="true"]');
      if (!submitBtn) return;

      const i18n = window.cardVariantsI18n || {};
      const mainSpan = submitBtn.querySelector('span:not(.sold-out-message):not(.icon-wrap)');
      const soldOutSpan = submitBtn.querySelector('.sold-out-message');

      if (variant.available) {
        submitBtn.disabled = false;
        if (mainSpan) mainSpan.textContent = i18n.add_to_cart || 'Add to cart';
        if (soldOutSpan) soldOutSpan.classList.add('hidden');
      } else {
        submitBtn.disabled = true;
        if (mainSpan) mainSpan.textContent = i18n.sold_out || 'Sold out';
        if (soldOutSpan) soldOutSpan.classList.remove('hidden');
      }
    }
  }

  /**
   * Actualiza el estado activo de las miniaturas:
   * - El thumbnail clicado pierde su href (pasa a ser no-clickeable).
   * - El thumbnail previamente activo recupera su href (vuelve a ser clickeable).
   */
  function updateThumbnails(cardWrapper, clickedBtn, originalProductUrl) {
    const currentActiveLi = cardWrapper.querySelector('.card-variants-custom__item.is-active');
    const currentActiveBtn = currentActiveLi?.querySelector('.card-variants-custom__btn');
    const clickedLi = clickedBtn.closest('.card-variants-custom__item');

    // El activo anterior vuelve a ser clickeable
    if (currentActiveBtn && currentActiveLi) {
      currentActiveLi.classList.remove('is-active');
      currentActiveBtn.href = originalProductUrl;
      currentActiveBtn.removeAttribute('aria-current');
    }

    // El clicado pasa a ser activo
    clickedLi.classList.add('is-active');
    clickedBtn.removeAttribute('href');
    clickedBtn.setAttribute('aria-current', 'true');
  }

  /** Lógica principal: obtiene datos del producto y actualiza el card */
  async function switchCardProduct(cardWrapper, clickedBtn) {
    // Guard contra swaps concurrentes
    if (cardWrapper.dataset.swapping) return;
    cardWrapper.dataset.swapping = '1';

    const productUrl = clickedBtn.href;
    const handle = clickedBtn.dataset.productHandle;
    if (!handle || !productUrl) {
      delete cardWrapper.dataset.swapping;
      return;
    }

    // Leer la URL actual ANTES de modificar el DOM
    const originalUrl = cardWrapper.querySelector('.card__heading a')?.getAttribute('href') || '';

    // Feedback inmediato: actualizar thumbnail activo sin esperar el fetch
    updateThumbnails(cardWrapper, clickedBtn, originalUrl);

    // Fade solo sobre la imagen mientras llega el fetch
    const media = cardWrapper.querySelector('.card__media');
    if (media) {
      media.style.transition = `opacity ${FADE_MS}ms ease`;
      media.style.opacity = '0.3';
    }

    let data, gender = '';
    try {
      const [productRes, genderRes] = await Promise.all([
        fetch(`/products/${handle}.js`),
        fetch(`/products/${handle}?view=rb-gender`),
      ]);
      if (!productRes.ok) throw new Error(`Fetch error: ${productRes.status}`);
      data = await productRes.json();
      if (genderRes.ok) {
        try { gender = JSON.parse(await genderRes.text()) || ''; } catch (_) {}
      }
    } catch (err) {
      console.error('[card-variants-custom] Error fetching product:', err);
      if (media) media.style.opacity = '1';
      delete cardWrapper.dataset.swapping;
      return;
    }

    updateImages(cardWrapper, data);
    updateLinksAndTitles(cardWrapper, data.url || `/products/${data.handle}`, data.title);
    updateVendor(cardWrapper, data.vendor, gender);
    updatePrice(cardWrapper, data.price, data.compare_at_price, data.price_varies);
    updateBadges(cardWrapper, data.available, data.price, data.compare_at_price);
    updateQuickAdd(cardWrapper, data);

    // Fade in de la imagen con el nuevo contenido
    if (media) {
      requestAnimationFrame(() => {
        media.style.opacity = '1';
      });
    }

    delete cardWrapper.dataset.swapping;
  }

  /** Event delegation — cubre todos los cards en la página */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.card-variants-custom__btn[href]');
    if (!btn) return;

    const li = btn.closest('.card-variants-custom__item');
    if (li?.classList.contains('is-active')) return;

    const cardWrapper = btn.closest('.card-wrapper');
    if (!cardWrapper) return;

    e.preventDefault();
    switchCardProduct(cardWrapper, btn);
  });

  /** Actualiza el estado disabled de los botones prev/next del slider */
  function updateSliderButtons(wrapper) {
    const track = wrapper.querySelector('.card-variants-custom__slider-track');
    const prevBtn = wrapper.querySelector('.card-variants-custom__slider-btn--prev');
    const nextBtn = wrapper.querySelector('.card-variants-custom__slider-btn--next');
    if (!track || !prevBtn || !nextBtn) return;
    prevBtn.disabled = track.scrollLeft <= 1;
    nextBtn.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
  }

  /** Event delegation para botones de navegación del slider */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.card-variants-custom__slider-btn--prev, .card-variants-custom__slider-btn--next');
    if (!btn) return;

    const wrapper = btn.closest('.card-variants-custom__slider-wrapper');
    const track = wrapper?.querySelector('.card-variants-custom__slider-track');
    const list = track?.querySelector('.card-variants-custom__list--slider');
    const item = list?.querySelector('.card-variants-custom__item');
    if (!track || !item) return;

    const gap = parseFloat(getComputedStyle(list).gap) || 0;
    const itemWidth = item.offsetWidth + gap;
    const isPrev = btn.classList.contains('card-variants-custom__slider-btn--prev');
    track.scrollLeft += isPrev ? -itemWidth : itemWidth;

    setTimeout(() => updateSliderButtons(wrapper), 320);
  });

  /** Actualiza botones al hacer scroll sobre el track */
  document.addEventListener('scroll', (e) => {
    if (!e.target.classList?.contains('card-variants-custom__slider-track')) return;
    const wrapper = e.target.closest('.card-variants-custom__slider-wrapper');
    if (wrapper) updateSliderButtons(wrapper);
  }, { capture: true, passive: true });

})();
